import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from './api/client';
import PageHeader from './components/PageHeader';
import FittingEditModal, { TITLE_MATCH_HINT } from './components/FittingEditModal';
import { formatPrice } from './utils/formatPrice';
import './ContractSeedingPage.css';

const REFRESH_COOLDOWN_SECONDS = 300;

function formatLastFetched(iso) {
  if (!iso) return 'Never';
  const date = new Date(iso);
  return date.toLocaleString();
}

function secondsUntilRefresh(lastFetched) {
  if (!lastFetched) return 0;
  const elapsed = (Date.now() - new Date(lastFetched).getTime()) / 1000;
  return Math.max(0, Math.ceil(REFRESH_COOLDOWN_SECONDS - elapsed));
}

function ContractSeedingDetailPage() {
  const { destinationId } = useParams();
  const navigate = useNavigate();

  const [destination, setDestination] = useState(null);
  const [entries, setEntries] = useState([]);
  const [editingFitting, setEditingFitting] = useState(null);
  const [counts, setCounts] = useState({});       // entry_id -> {total_count, my_count, to_fill}
  const [lastFetched, setLastFetched] = useState(null);
  const [settings, setSettings] = useState({ isk_per_m3: 0, collateral_percentage: 0, broker_fee_percentage: 0, sales_tax_percentage: 0, desired_roi_percentage: 10, contracting_character_ids: [] });
  const [allFittings, setAllFittings] = useState([]);
  const [allCharacters, setAllCharacters] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [selectedFittingId, setSelectedFittingId] = useState('');
  const [newTargetStock, setNewTargetStock] = useState(1);
  const [addingEntry, setAddingEntry] = useState(false);
  const [addEntryError, setAddEntryError] = useState('');

  const [savingSettings, setSavingSettings] = useState(false);
  const [editSettings, setEditSettings] = useState(null);  // null = not editing

  const [copiedEntryId, setCopiedEntryId] = useState(null);
  const [copiedPriceEntryId, setCopiedPriceEntryId] = useState(null);

  // Purchase cluster state
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [pinnedClusterDate, setPinnedClusterDate] = useState(null);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [refreshingWalletTransactions, setRefreshingWalletTransactions] = useState(false);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchDestination = useCallback(async () => {
    const response = await client.get('/seeding/destinations/');
    const dest = (response.data.destinations || []).find(d => d.id === parseInt(destinationId));
    if (!dest) navigate('/contracts');
    setDestination(dest);
  }, [destinationId, navigate]);

  const fetchEntries = useCallback(async () => {
    const response = await client.get(`/contracts/destinations/${destinationId}/entries/`);
    setEntries(response.data.entries || []);
  }, [destinationId]);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await client.get(`/contracts/destinations/${destinationId}/counts/`);
      const countsMap = {};
      for (const row of response.data.counts || []) {
        countsMap[row.entry_id] = row;
      }
      setCounts(countsMap);
      setLastFetched(response.data.last_fetched);
      setCooldownRemaining(secondsUntilRefresh(response.data.last_fetched));
      setPinnedClusterDate(response.data.pinned_cluster_date || null);
    } catch {
      // Counts may be empty if no contracts cached yet — non-fatal
    }
  }, [destinationId]);

  const fetchSettings = useCallback(async () => {
    const response = await client.get(`/contracts/destinations/${destinationId}/settings/`);
    setSettings(response.data);
  }, [destinationId]);

  const fetchFittings = useCallback(async () => {
    const response = await client.get('/contracts/fittings/');
    setAllFittings(response.data.fittings || []);
  }, []);

  const fetchCharacters = useCallback(async () => {
    const response = await client.get('/auth/characters/');
    setAllCharacters(response.data.characters || []);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDestination(),
          fetchEntries(),
          fetchCounts(),
          fetchSettings(),
          fetchFittings(),
          fetchCharacters(),
        ]);
      } catch (err) {
        if (err.response?.status === 401) navigate('/');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchDestination, fetchEntries, fetchCounts, fetchSettings, fetchFittings, fetchCharacters, navigate]);

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      setCooldownRemaining(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError('');
    try {
      const response = await client.post(`/contracts/destinations/${destinationId}/refresh/`);
      const now = response.data.last_fetched || new Date().toISOString();
      setLastFetched(now);
      setCooldownRemaining(REFRESH_COOLDOWN_SECONDS);
      if (response.data.warnings?.length) {
        setRefreshError(response.data.warnings.join('; '));
      }
      await fetchCounts();
    } catch (err) {
      setRefreshError(err.response?.data?.error || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefreshWalletTransactions = async () => {
    setRefreshingWalletTransactions(true);
    try {
      await client.post(`/contracts/destinations/${destinationId}/refresh-wallet-transactions/`);
    } catch (err) {
      setRefreshError(err.response?.data?.error || 'Failed to refresh wallet transactions');
    } finally {
      setRefreshingWalletTransactions(false);
    }
  };

  const openClusterModal = async () => {
    setLoadingClusters(true);
    setShowClusterModal(true);
    try {
      const response = await client.get(`/contracts/destinations/${destinationId}/purchase-clusters/`);
      setClusters(response.data.clusters || []);
      setPinnedClusterDate(response.data.pinned_cluster_date || null);
    } catch (err) {
      console.error('Error loading purchase clusters:', err);
    } finally {
      setLoadingClusters(false);
    }
  };

  const selectCluster = async (clusterDate) => {
    try {
      await client.post(`/contracts/destinations/${destinationId}/set-price-cluster/`, { cluster_date: clusterDate });
      setPinnedClusterDate(clusterDate);
      setShowClusterModal(false);
      await fetchCounts();
    } catch (err) {
      console.error('Error setting price cluster:', err);
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!selectedFittingId) { setAddEntryError('Select a fitting'); return; }
    setAddingEntry(true);
    setAddEntryError('');
    try {
      const response = await client.post(`/contracts/destinations/${destinationId}/entries/add/`, {
        fitting_id: parseInt(selectedFittingId),
        target_stock: parseInt(newTargetStock) || 1,
      });
      setEntries(prev => [...prev, response.data]);
      setShowAddEntryModal(false);
      setSelectedFittingId('');
      setNewTargetStock(1);
      await fetchCounts();
    } catch (err) {
      setAddEntryError(err.response?.data?.error || 'Failed to add entry');
    } finally {
      setAddingEntry(false);
    }
  };

  const handleFittingSaved = async (saved) => {
    setEntries(prev => prev.map(e => (e.fitting.id === saved.id ? { ...e, fitting: { ...e.fitting, ...saved } } : e)));
    setAllFittings(prev => prev.map(f => (f.id === saved.id ? { ...f, ...saved } : f)));
    await fetchCounts();
  };

  const handleRemoveEntry = async (entryId, fittingName) => {
    if (!window.confirm(`Remove "${fittingName}" from this destination?`)) return;
    try {
      await client.delete(`/contracts/destinations/${destinationId}/entries/${entryId}/remove/`);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setCounts(prev => { const next = { ...prev }; delete next[entryId]; return next; });
    } catch {
      alert('Failed to remove entry');
    }
  };

  const handleTargetStockChange = async (entryId, newStock) => {
    const val = parseInt(newStock);
    if (isNaN(val) || val < 0) return;
    try {
      await client.patch(`/contracts/destinations/${destinationId}/entries/${entryId}/update/`, {
        target_stock: val,
      });
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, target_stock: val } : e));
      await fetchCounts();
    } catch {
      alert('Failed to update target stock');
    }
  };

  const handleToggleCharacter = async (characterId, currentlySelected) => {
    const currentIds = settings.contracting_character_ids;
    const newIds = currentlySelected
      ? currentIds.filter(id => id !== characterId)
      : [...currentIds, characterId];

    try {
      const response = await client.patch(`/contracts/destinations/${destinationId}/settings/update/`, {
        contracting_character_ids: newIds,
      });
      setSettings(response.data);
    } catch {
      alert('Failed to update character selection');
    }
  };

  const handleSaveSettings = async () => {
    if (!editSettings) return;
    setSavingSettings(true);
    try {
      const response = await client.patch(`/contracts/destinations/${destinationId}/settings/update/`, {
        isk_per_m3: parseFloat(editSettings.isk_per_m3) || 0,
        collateral_percentage: parseFloat(editSettings.collateral_percentage) || 0,
        broker_fee_percentage: parseFloat(editSettings.broker_fee_percentage) || 0,
        sales_tax_percentage: parseFloat(editSettings.sales_tax_percentage) || 0,
        desired_roi_percentage: parseFloat(editSettings.desired_roi_percentage) || 0,
      });
      setSettings(response.data);
      setEditSettings(null);
    } catch {
      alert('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCopyItems = async (entry) => {
    const lines = [
      `${entry.fitting.ship_type_name}\t1`,
      ...entry.fitting.parsed_items.map(item => `${item.type_name}\t${item.quantity}`),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(lines);
      setCopiedEntryId(entry.id);
      setTimeout(() => setCopiedEntryId(null), 2000);
    } catch {
      alert('Copy failed — check browser permissions');
    }
  };

  const handleCopyListPrice = async (entryId, listPrice) => {
    try {
      await navigator.clipboard.writeText(Math.round(listPrice).toString());
      setCopiedPriceEntryId(entryId);
      setTimeout(() => setCopiedPriceEntryId(null), 2000);
    } catch {
      alert('Copy failed — check browser permissions');
    }
  };

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const addedFittingIds = new Set(entries.map(e => e.fitting.id));
  const availableFittings = allFittings.filter(f => !addedFittingIds.has(f.id));

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Contract Seeding" />
        <div className="page-status">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={destination?.destination_name}
        crumb={{ to: '/contracts', label: 'Contract Seeding' }}
        meta={destination && [destination.destination_system, destination.destination_region]}
      />
      <div className="page-content">
        <div className="contract-page-grid">
          <section className="panel">
            <div className="panel-head">
              <h2>Destination settings</h2>
              <span className="panel-sub">
                {destination?.is_structure
                  ? 'SCC surcharge (0.5%) is added to sales tax at player structures.'
                  : 'No SCC surcharge at NPC stations.'}
              </span>
              <div className="panel-actions">
                {editSettings === null
                  ? <button className="btn btn-ghost btn-sm" onClick={() => setEditSettings({ isk_per_m3: settings.isk_per_m3, collateral_percentage: settings.collateral_percentage, broker_fee_percentage: settings.broker_fee_percentage, sales_tax_percentage: settings.sales_tax_percentage, desired_roi_percentage: settings.desired_roi_percentage })}>Edit</button>
                  : (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditSettings(null)}>Cancel</button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveSettings} disabled={savingSettings}>
                        {savingSettings ? 'Saving…' : 'Save'}
                      </button>
                    </>
                  )}
              </div>
            </div>
            <div className="panel-body stat-grid">
              {[
                ['isk_per_m3', 'Freight', 'ISK/m³', v => `${Number(v).toLocaleString()} ISK/m³`, { min: 0 }],
                ['collateral_percentage', 'Collateral', '%', v => `${v}%`, { min: 0, max: 100, step: 0.1 }],
                ['broker_fee_percentage', 'Broker fee', '%', v => `${v}%`, { min: 0, max: 100, step: 0.01 }],
                ['sales_tax_percentage', 'Sales tax', '%', v => `${v}%`, { min: 0, max: 100, step: 0.01 }],
                ['desired_roi_percentage', 'Target ROI', '%', v => `${v}%`, { min: 0, max: 100, step: 0.1 }],
              ].map(([key, label, unit, show, limits]) => (
                <div className="stat" key={key}>
                  <span>{label}</span>
                  {editSettings === null
                    ? <b>{show(settings[key])}</b>
                    : (
                      <div className="input-unit">
                        <input
                          type="number"
                          {...limits}
                          value={editSettings[key]}
                          onChange={e => setEditSettings(prev => ({ ...prev, [key]: e.target.value }))}
                          aria-label={label}
                        />
                        <span>{unit}</span>
                      </div>
                    )}
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>Contracting characters</h2>
              <span className="panel-sub">{settings.contracting_character_ids.length} selected</span>
            </div>
            <div className="panel-body char-chips">
              {allCharacters.length === 0 && <span className="text-dim">No characters found.</span>}
              {allCharacters.map(char => {
                const selected = settings.contracting_character_ids.includes(char.character_id);
                return (
                  <label key={char.character_id} className={`char-chip${selected ? ' selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleToggleCharacter(char.character_id, selected)}
                    />
                    {char.portrait_64x64 && <img src={char.portrait_64x64} alt="" className="char-chip-portrait" />}
                    <span>
                      <b>{char.character_name}</b>
                      <small>{char.corporation_name}{char.is_primary && ' · primary'}</small>
                    </span>
                  </label>
                );
              })}
              {settings.contracting_character_ids.length === 0 && (
                <span className="text-warn">Select at least one character to fetch contracts.</span>
              )}
            </div>
          </section>
        </div>

        <section className="panel">
          <div className="panel-head">
            <h2>Contract entries</h2>
            <span className="panel-sub">Contracts last fetched {formatLastFetched(lastFetched)}</span>
            <div className="panel-actions">
              <button className="btn btn-ghost" onClick={openClusterModal} title="Shopping run used as cost basis">
                Cluster: {pinnedClusterDate ? new Date(pinnedClusterDate).toLocaleString() : 'Auto (most recent)'} ▾
              </button>
              <button className="btn" onClick={handleRefreshWalletTransactions} disabled={refreshingWalletTransactions}>
                {refreshingWalletTransactions ? 'Refreshing…' : 'Refresh wallet'}
              </button>
              <button className="btn" onClick={handleRefresh} disabled={refreshing || cooldownRemaining > 0}>
                {refreshing ? 'Refreshing…' : 'Refresh contracts'}
                {!refreshing && cooldownRemaining > 0 && <span className="btn-cooldown">{cooldownRemaining}s</span>}
              </button>
              <button onClick={() => setShowAddEntryModal(true)} className="btn btn-primary">
                Add fitting
              </button>
            </div>
          </div>

          {refreshError && <div className="notice notice-warn contract-notice">{refreshError}</div>}

          {entries.length === 0 ? (
            <div className="table-empty">No contract entries yet. Use "Add fitting" to start.</div>
          ) : (
            <div className="table-scroll">
              <table className="data-table contract-entries-table">
                <thead>
                  <tr>
                    <th>Ship</th>
                    <th>Fitting</th>
                    <th className="num">Target</th>
                    <th className="num" title={TITLE_MATCH_HINT}>Listed (all)</th>
                    <th className="num" title={TITLE_MATCH_HINT}>Listed (yours)</th>
                    <th className="num">To fill</th>
                    <th className="num">Paid</th>
                    <th className="num">List at</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => {
                    const c = counts[entry.id] || { total_count: 0, my_count: 0, to_fill: entry.target_stock };
                    const missing = (
                      <span className="pill pill-warn" title={c.unresolved_items?.join(', ') || 'No purchase price data'}>No cost data</span>
                    );
                    return (
                      <tr key={entry.id}>
                        <td><b>{entry.fitting.ship_type_name}</b></td>
                        <td>{entry.fitting.name}</td>
                        <td className="num">
                          <input
                            type="number"
                            min="0"
                            value={entry.target_stock}
                            onChange={e => handleTargetStockChange(entry.id, e.target.value)}
                            className="contract-target-input"
                            aria-label="Target stock"
                          />
                        </td>
                        <td className="num text-dim">{c.total_count}</td>
                        <td className="num">{c.my_count}</td>
                        <td className={`num ${c.to_fill > 0 ? 'text-warn' : 'text-faint'}`}>{c.to_fill}</td>
                        <td className="num">{c.purchased_cost != null ? formatPrice(c.purchased_cost) : missing}</td>
                        <td className="num">
                          {c.list_price != null
                            ? (
                              <button
                                className={`btn btn-ghost btn-sm ${copiedPriceEntryId === entry.id ? 'is-copied' : ''}`}
                                onClick={() => handleCopyListPrice(entry.id, c.list_price)}
                                title="Copy list price"
                              >
                                {copiedPriceEntryId === entry.id ? 'Copied' : formatPrice(c.list_price)}
                              </button>
                            )
                            : missing}
                        </td>
                        <td className="num">
                          <span className="btn-row settings-actions">
                            <button
                              className={`btn btn-sm ${copiedEntryId === entry.id ? 'is-copied' : ''}`}
                              onClick={() => handleCopyItems(entry)}
                              title="Copy the fitting's items to the clipboard"
                            >
                              {copiedEntryId === entry.id ? 'Copied' : 'Copy items'}
                            </button>
                            <span className="row-action btn-row">
                              <button className="btn btn-sm" onClick={() => setEditingFitting(entry.fitting)}>Edit</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleRemoveEntry(entry.id, entry.fitting.name)}>Remove</button>
                            </span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Add Entry Modal */}
        {showAddEntryModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Add Contract Entry</h3>
                <button onClick={() => { setShowAddEntryModal(false); setSelectedFittingId(''); setNewTargetStock(1); setAddEntryError(''); }} className="icon-btn modal-close">×</button>
              </div>
              <form onSubmit={handleAddEntry}>
                <div className="form-group">
                  <label>Fitting:</label>
                  {availableFittings.length === 0 ? (
                    <p className="dim-text">All fittings are already added to this destination.</p>
                  ) : (
                    <select
                      value={selectedFittingId}
                      onChange={e => setSelectedFittingId(e.target.value)}
                      autoFocus
                    >
                      <option value="">— Select a fitting —</option>
                      {availableFittings.map(f => (
                        <option key={f.id} value={f.id}>{f.ship_type_name} — {f.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label>Target Stock:</label>
                  <input
                    type="number"
                    min="0"
                    value={newTargetStock}
                    onChange={e => setNewTargetStock(e.target.value)}
                  />
                </div>
                {addEntryError && <div className="error-message">{addEntryError}</div>}
                <div className="modal-actions">
                  <button type="button" onClick={() => { setShowAddEntryModal(false); setSelectedFittingId(''); setNewTargetStock(1); setAddEntryError(''); }} className="btn btn-ghost">Cancel</button>
                  <button type="submit" disabled={addingEntry || !selectedFittingId} className="btn btn-primary">
                    {addingEntry ? 'Adding…' : 'Add Entry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showClusterModal && (
          <div className="modal-overlay" onClick={() => setShowClusterModal(false)}>
            <div className="modal-content cluster-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Select Purchase Cluster</h3>
                <button className="icon-btn modal-close" aria-label="Close" onClick={() => setShowClusterModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p className="cluster-modal-hint">
                  Choose which shopping run to use as the cost basis for this destination's fittings.
                  The most recent cluster is used by default.
                </p>
                {loadingClusters ? (
                  <div className="cluster-loading">Loading clusters...</div>
                ) : clusters.length === 0 ? (
                  <div className="cluster-empty">No purchase clusters found in the last 30 days.</div>
                ) : (
                  <div className="cluster-list">
                    <div
                      className={`cluster-item${!pinnedClusterDate ? ' cluster-item--selected' : ''}`}
                      onClick={() => selectCluster(null)}
                    >
                      <div className="cluster-item-date">Auto (most recent)</div>
                      <div className="cluster-item-meta">Always uses the newest cluster</div>
                    </div>
                    {clusters.filter(c => c.matching_item_count > 0).map((cluster, idx) => {
                      const isSelected = pinnedClusterDate === cluster.cluster_date;
                      return (
                        <div
                          key={idx}
                          className={`cluster-item${isSelected ? ' cluster-item--selected' : ''}`}
                          onClick={() => selectCluster(cluster.cluster_date)}
                        >
                          <div className="cluster-item-date">
                            {new Date(cluster.cluster_date).toLocaleString()}
                          </div>
                          <div className="cluster-item-meta">
                            {cluster.matching_item_count} matching components &middot; {cluster.total_transactions} total txns
                          </div>
                          {cluster.matching_items.length > 0 && (
                            <div className="cluster-item-prices">
                              {cluster.matching_items.slice(0, 5).map(item => (
                                <span key={item.type_id} className="cluster-price-tag">
                                  {item.name}: {parseFloat(item.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              ))}
                              {cluster.matching_items.length > 5 && (
                                <span className="cluster-price-tag cluster-price-tag--more">
                                  +{cluster.matching_items.length - 5} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {editingFitting && (
          <FittingEditModal
            fitting={editingFitting}
            onClose={() => setEditingFitting(null)}
            onSaved={handleFittingSaved}
          />
        )}
      </div>
    </div>
  );
}

export default ContractSeedingDetailPage;
